/**
 * Split records into transmissions that stay inside the IRS limits.
 *
 * IRIS caps a transmission at 100 MB (Pub 5718). Tab Service sends up to 20,000 records per
 * transmission, the size its legacy A2A path has filed against IRIS: a 60,000-record file is
 * three transmissions rather than sixty. The byte estimate is deliberately crude and
 * pessimistic: the JSON length of a record times three comfortably covers the XML expansion,
 * and being early is harmless while being late costs a rejected transmission.
 *
 * A replacement adds a second reason to split. It re-sends a rejected transmission, and the
 * IRS matches it by the receipt of the transmission it replaces, so records that were
 * rejected in two different transmissions can never travel together however small they are.
 * `groupBy` is that rule: records are grouped first, and each group is then chunked on its
 * own, so a chunk never spans two groups.
 */
export interface ChunkLimits<T = unknown> {
  maxRecords?: number;
  maxBytes?: number;
  /**
   * Optional grouping key. Records that answer differently here are never put in the same
   * transmission. Groups come out in the order they were first seen.
   */
  groupBy?: (record: T, index: number) => string;
}

export const DEFAULT_MAX_RECORDS = 20_000;
export const DEFAULT_MAX_BYTES = 100 * 1024 * 1024;

function estimateBytes(record: unknown): number {
  try {
    return JSON.stringify(record ?? {}).length * 3;
  } catch {
    return 4096;
  }
}

/**
 * Records grouped by a key, in first-seen order.
 *
 * Kept separate from `chunkRecords` because a caller often needs the key itself: the send
 * drainer has to know which rejected transmission a replacement chunk stands in for, and
 * that is the group key, not the chunk number.
 */
export function groupRecords<T>(
  records: T[],
  keyFn: (record: T, index: number) => string
): Array<{ key: string; records: T[] }> {
  const groups: Array<{ key: string; records: T[] }> = [];
  const byKey = new Map<string, { key: string; records: T[] }>();
  if (!Array.isArray(records)) return groups;

  records.forEach((record, index) => {
    const key = String(keyFn(record, index));
    let group = byKey.get(key);
    if (!group) {
      group = { key, records: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.records.push(record);
  });
  return groups;
}

function chunkOneGroup<T>(records: T[], maxRecords: number, maxBytes: number): T[][] {
  const chunks: T[][] = [];
  let current: T[] = [];
  let bytes = 0;

  for (const record of records) {
    const size = estimateBytes(record);
    const wouldOverflow =
      current.length >= maxRecords || (current.length > 0 && bytes + size > maxBytes);
    if (wouldOverflow) {
      chunks.push(current);
      current = [];
      bytes = 0;
    }
    current.push(record);
    bytes += size;
  }
  if (current.length) chunks.push(current);
  return chunks;
}

export function chunkRecords<T>(records: T[], limits: ChunkLimits<T> = {}): T[][] {
  const maxRecords = Math.max(1, limits.maxRecords ?? DEFAULT_MAX_RECORDS);
  const maxBytes = Math.max(1, limits.maxBytes ?? DEFAULT_MAX_BYTES);
  if (!Array.isArray(records) || records.length === 0) return [];

  if (!limits.groupBy) return chunkOneGroup(records, maxRecords, maxBytes);

  const out: T[][] = [];
  for (const group of groupRecords(records, limits.groupBy)) {
    out.push(...chunkOneGroup(group.records, maxRecords, maxBytes));
  }
  return out;
}
